import { useState, useEffect } from 'react';
import { Fetch } from '../helpers/fetchWrapper';
import { useCancellablePromise } from '../helpers/promiseHandler';
const STATUS = {
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error',
};

const jobService = {
  _url: `${process.env.REACT_APP_LEAD_API_URL}/job`,

  GetAllJobs(searchQuery, fetch) {
    const [jobs, setJobs] = useState({
      jobs: [],
      totalItems: 0,
    });
    const { cancellablePromise } = useCancellablePromise();
    const [status, setStatus] = useState(STATUS.LOADING);
    useEffect(() => {
      setStatus(STATUS.LOADING);
      cancellablePromise(this.getJobs(searchQuery))
        .then(res => {
          setJobs(() => res);
          setStatus(STATUS.SUCCESS);
        })
        .catch(() => setStatus(STATUS.ERROR));
    }, [JSON.stringify(searchQuery), fetch]);
    return {
      jobs_loading: status === STATUS.LOADING,
      jobs_error: status === STATUS.ERROR,
      jobs_data: jobs,
    };
  },

  GetAllJobLeads(searchQuery, fetch) {
    const [leads, setLeads] = useState({
      leads: [],
      totalItems: 0,
    });
    const { cancellablePromise } = useCancellablePromise();
    const [status, setStatus] = useState(STATUS.LOADING);
    useEffect(() => {
      setStatus(STATUS.LOADING);
      cancellablePromise(this.getAllJobLeads(searchQuery))
        .then(res => {
          setLeads(() => res);
          setStatus(STATUS.SUCCESS);
        })
        .catch(() => setStatus(STATUS.ERROR));
    }, [JSON.stringify(searchQuery), fetch]);

    return {
      leads_loading: status === STATUS.LOADING,
      leads_error: status === STATUS.ERROR,
      leads_data: leads,
    };
  },

  async addLeadsToJob(payload) {
    let res = await Fetch.post(`${this._url}/add-leads`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
  async getJobs({ page = 1, itemsPerPage = 10, searchText = '', getAll = false, startDate = '', endDate = '' }) {
    let res = await Fetch.get(
      `${this._url}/get-all-jobs?page=${page}&itemsPerPage=${itemsPerPage}&searchText=${searchText}&getAll=${getAll}&startDate=${startDate}&endDate=${endDate}`,
    );
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something Went Wrong');
  },

  async getAllJobLeads({
    page = 1,
    itemsPerPage = 10,
    searchText = '',
    getAll = false,
    id = '',
    status = [],
    contactedStatus = 'ALL',
    source = [],
    campaigns = [],
    tagsFilter = [],
    listsFilter = [],
    dateCreatedFilter = '',
    jobTitle = '',
    companyName = '',
    language = '',
    location = '',
    industry = '',
    headline = '',
    emailExists = 'ALL',
    linkedinProfileExists = 'ALL',
    openToWork = 'ALL',
    isHiring = 'ALL',
  }) {
    const statusParam =
      Array.isArray(status) && status.length > 0 ? `&status=${encodeURIComponent(status.join(','))}` : '';
    const contactedStatusParam = contactedStatus ? `&contactedStatus=${encodeURIComponent(contactedStatus)}` : '';
    const sourceArray = Array.isArray(source) ? source : source ? [source] : [];
    const sourceParam = sourceArray.length > 0 ? `&source=${encodeURIComponent(sourceArray.join(','))}` : '';
    const campaignsArray = Array.isArray(campaigns) ? campaigns : campaigns ? [campaigns] : [];
    const campaignsParam =
      campaignsArray.length > 0 ? `&campaigns=${encodeURIComponent(campaignsArray.join(','))}` : '';
    const tagsArray = Array.isArray(tagsFilter) ? tagsFilter : tagsFilter ? [tagsFilter] : [];
    const tagsParam = tagsArray.length > 0 ? `&tags=${encodeURIComponent(tagsArray.join(','))}` : '';
    const listsArray = Array.isArray(listsFilter) ? listsFilter : listsFilter ? [listsFilter] : [];
    const listsParam = listsArray.length > 0 ? `&lists=${encodeURIComponent(listsArray.join(','))}` : '';
    const dateCreatedParam = dateCreatedFilter ? `&date_created=${encodeURIComponent(dateCreatedFilter)}` : '';
    const companyNameParam = companyName ? `&company_name=${encodeURIComponent(companyName)}` : '';
    const jobTitleParam = jobTitle ? `&job_title=${encodeURIComponent(jobTitle)}` : '';
    const languageParam = language ? `&language=${encodeURIComponent(language)}` : '';
    const locationParam = location ? `&location=${encodeURIComponent(location)}` : '';
    const industryParam = industry ? `&industry=${encodeURIComponent(industry)}` : '';
    const headlineParam = headline ? `&headline=${encodeURIComponent(headline)}` : '';

    const emailExistsParam =
      emailExists === 'EXISTS'
        ? `&email_exists=true`
        : emailExists === 'NOT_EXISTS'
          ? `&email_exists=false`
          : '';

    const linkedinProfileExistsParam =
      linkedinProfileExists === 'EXISTS'
        ? `&linkedin_url_exists=true`
        : linkedinProfileExists === 'NOT_EXISTS'
          ? `&linkedin_url_exists=false`
          : '';

    const openToWorkParam =
      openToWork === 'YES' ? `&open_to_work=true` : openToWork === 'NO' ? `&open_to_work=false` : '';

    const isHiringParam = isHiring === 'YES' ? `&is_hiring=true` : isHiring === 'NO' ? `&is_hiring=false` : '';

    let res = await Fetch.get(
      `${this._url}/get-job-leads/${id}?page=${page}&itemsPerPage=${itemsPerPage}&searchText=${searchText}&getAll=${getAll}${statusParam}${contactedStatusParam}${sourceParam}${campaignsParam}${tagsParam}${listsParam}${dateCreatedParam}${companyNameParam}${jobTitleParam}${languageParam}${locationParam}${industryParam}${headlineParam}${emailExistsParam}${linkedinProfileExistsParam}${openToWorkParam}${isHiringParam}`,
    );
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something Went Wrong');
  },

  async updateJobName({ id, name }) {
    let res = await Fetch.patch(`${this._url}/update-job-name/${id}`, { name });
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Failed to update job name');
  },

  async previewJobLeadsToCampaign(payload) {
    let res = await Fetch.post(`${this._url}/preview-campaign-job-leads`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async previewJobLeadsToSublist(payload) {
    let res = await Fetch.post(`${this._url}/preview-sublist-job-leads`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async addLeadsFromJobToList(payload) {
    let res = await Fetch.post(`${this._url}/add-job-leads-to-sublist`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async removeLeadsFromJob(payload) {
    let res = await Fetch.post(`${this._url}/remove-leads-from-job`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Failed to remove leads from job');
  },


  async deleteJob(id) {
    let res = await Fetch.delete(`${this._url}/delete-job/${id}`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Failed to delete job');
  },

  async downloadJob({ id }) {
    let res = await Fetch.get(`${this._url}/download-job/${id}`);
    if (res.status >= 200 && res.status < 300) {
      const blob = await res.blob();
      return { blob };
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Failed to download job');
  },


};

export default jobService;
